import { Result } from '@/lib/type';
import CloudProviderCategory, {
  CloudPlatform,
  getAvailablePlatforms,
  normalizePlatform,
  SUPPORTED_PLATFORMS,
} from '@/pages/admin/settings/cloud_provider/CloudProviderCategory';
import { useQueryPublicCPs } from '@/pages/admin/settings/cloud_provider/hook';
import { Subtitle } from '@/pages/admin/settings/cloud_provider/StyledComponents';
import { formatRegionLabel } from '@/pages/admin/settings/cloud_provider/region';
import { IFormInput } from '@/pages/admin/settings/cloud_provider/types';
import { getMeta } from '@/pages/workgroup/api';
import { WorkSpaceMeta } from '@/pages/workgroup/type';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { FormControl } from '@tigergraph/app-ui-lib/form-control';
import { Input } from '@tigergraph/app-ui-lib/input';
import { Select } from '@tigergraph/app-ui-lib/select';
import { AxiosError } from 'axios';
import { useEffect, useMemo } from 'react';
import { Control, Controller, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useQuery } from 'react-query';

interface SelectVendorProps {
  cloudPlatform: CloudPlatform;
  onSelectPlatform: (platform: CloudPlatform) => void;
  control: Control<IFormInput, any>;
  errors: FieldErrors<IFormInput>;
  setValue: UseFormSetValue<IFormInput>;
  watch: UseFormWatch<IFormInput>;
}

export default function SelectVendor({
  cloudPlatform,
  onSelectPlatform,
  control,
  errors,
  setValue,
  watch,
}: SelectVendorProps) {
  const [css, theme] = useStyletron();

  const metaQuery = useQuery<Result<WorkSpaceMeta>, AxiosError>(['workspaceMeta'], () => getMeta());
  const regionOps = useMemo(() => {
    const platformRegions =
      metaQuery.data?.Result?.regions?.find((p) => normalizePlatform(p.platform) === cloudPlatform)?.regions || [];
    return platformRegions.map((region) => ({
      id: region.name,
      label: formatRegionLabel(region.name, region.description),
    }));
  }, [metaQuery.data, cloudPlatform]);

  // AWS is always enabled; other platforms are enabled once they have a public cloud provider
  // and a supported TigerGraph version.
  const publicCPsQuery = useQueryPublicCPs();
  const availablePlatforms = useMemo(
    () => getAvailablePlatforms(publicCPsQuery.data?.Result || [], metaQuery.data?.Result?.tgVersions || []),
    [publicCPsQuery.data, metaQuery.data]
  );
  const isPlatformEnabled = (platform: CloudPlatform) =>
    platform === CloudPlatform.AWS || availablePlatforms.has(platform);

  const region = watch('region');

  useEffect(() => {
    if (regionOps.length && !region) {
      setValue('region', regionOps[0].id);
    }
  }, [regionOps, region, setValue]);

  return (
    <>
      <Subtitle>Name</Subtitle>
      <FormControl label="" error={errors?.name?.message}>
        <Controller
          rules={{
            required: 'Required',
          }}
          control={control}
          name="name"
          render={({ field }) => (
            <Input id="name" placeholder="Enter cloud provider name" {...field} error={!!errors?.name} />
          )}
        />
      </FormControl>

      <Subtitle>Select Cloud Vendor</Subtitle>
      <div
        className={css({
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 140px), 1fr))',
          gap: '12px',
          marginBottom: '16px',
        })}
      >
        {SUPPORTED_PLATFORMS.map((name) => (
          <CloudProviderCategory
            key={name}
            disabled={!isPlatformEnabled(name)}
            selected={name == cloudPlatform}
            platform={name}
            onSelect={() => {
              if (name !== cloudPlatform) {
                onSelectPlatform(name);
                // Clear the region so the first region of the new platform gets selected.
                setValue('region', '');
              }
            }}
          />
        ))}
      </div>

      <Subtitle>Region</Subtitle>
      <FormControl>
        <Controller
          control={control}
          name="region"
          render={({ field: { value, onChange, ref, ...field } }) => (
            <Select
              options={regionOps}
              value={[{ id: value }]}
              onChange={(params) => {
                onChange(params.value[0].id);
              }}
              clearable={false}
              inputRef={ref}
              {...field}
              overrides={{
                Dropdown: {
                  style: {
                    maxHeight: '250px',
                    overflowY: 'auto',
                  },
                },
              }}
            />
          )}
        />
      </FormControl>
    </>
  );
}
